/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { Honeybadger } from '@honeybadger-io/react';

import { interimApi } from 'api/InterimApi';

import { ia } from 'lib/helpers/utility';

import ErrorPayment from 'components/Patient/checkin/SecurePayment/ErrorPayment';
import { Alert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Button/Button';
import ErrorBoundary, { withErrorBoundary } from 'components/shared/Error/Boundary';
import Loader from 'components/shared/Loader/Loader';

const DEFAULT_ERROR_MSG = `An unexpected error has occurred. Please try again later.`;

const LightboxWrapper = ({
  f_name,
  l_name,
  billingAddress,
  amount,
  patientId,
  practiceId,
  appointmentId,
  invoiceId,
  tokenOnly,
  saveCard,
  surcharge,
  setReceipt = () => {},
  invoiceToken,
  preAuth,
  preAuthForm,
  setCardId,
  updateAmount = () => {},
  onApproval = () => {
    console.warn('onApproval not handled for LightboxWrapper.jsx');
  },
  onCancel = () => {
    console.warn('onCancel not handled for LightboxWrapper.jsx');
  },
  onFail = () => {
    console.warn('onFail not handled for LightboxWrapper.jsx');
  },
  invoices,
  selectedVtid,
  SuccessScreen,
  payerId
}) => {
  const [token, setToken] = useState(null);
  const [show, setShow] = useState({
    status: '',
    retryTransaction: false,
    approved: false,
    unavailable: false,
    errors: []
  });
  const [txnId, setTxnId] = useState();
  const navigate = useNavigate();
  const outletContext = useOutletContext();

  useEffect(() => {
    initiateLightbox();
    // initiateTransaction();
  }, []);

  useEffect(() => {
    if (outletContext?.setDisableContinue) {
      outletContext?.setDisableContinue(show.unavailable);

      return () => {
        outletContext?.setDisableContinue(false);
      };
    }
  }, [show]);

  const initiateLightbox = async () => {
    try {
      const res = await interimApi(
        '/api/transactions/online/getCredentials',
        {
          f_name,
          l_name,
          amount,
          patientId,
          practiceId,
          invoiceId,
          appointmentId,
          saveCard,
          tokenOnly,
          billingAddress,
          invoiceToken,
          preAuth,
          surcharge,
          payer_id: payerId
        },
        navigate
      );
      const { code, error, token: loadedToken, transactionId: loadedTransactionId } = res.data;
      switch (code) {
        // case -1:
        //   navigate(redirect);
        //   break;
        case 0:
          if (loadedToken) {
            setToken(loadedToken);
            openLightbox(loadedToken, loadedTransactionId);
            // initiateTransaction(loadedToken);
          } else {
            // need to check if a selection is needed to be made
            console.error(`Lightbox token not received.`);
            onFail(
              error ||
                `There's been an error. Please try again later. If the issue persists please contact support.`
            );
          }
          break;
        case 1:
          if (error) {
            if (Array.isArray(error)) {
              setShow({ ...show, unavailable: true, error });
            } else {
              setShow({ ...show, unavailable: true, error: [error] });
            }
          }
          break;
        default:
          onFail(error || DEFAULT_ERROR_MSG);
          break;
      }
    } catch (error) {
      onFail(`An unexpected error has occurred. Please try again later.`);
      Honeybadger.notify(`There's been an unexpected error, please try again later. ${error}`);
    }
  };

  const openLightbox = async (token, transactionId) => {
    try {
      const callback = {
        onError(error) {
          onFail(
            error ||
              `There's been an error. Please try again later. If the issue persists please contact support.`
          );
          console.error(`Lightbox Error: ${error}`);
          setShow({ ...show, retryTransaction: 'Retry' });
          // showResult('error', error);
          completeTransaction(
            transactionId,
            { txnStatus: 'error', ...error },
            `Failed to finalize transaction. Please try again later.`
          );
        },
        onCancelled() {
          setShow({ ...show, retryTransaction: 'Retry' });
          onCancel();
          // showResult('cancelled', '');
          completeTransaction(transactionId, { txnStatus: 'cancelled' });
        },
        onDeclined(response) {
          setShow({ ...show, retryTransaction: 'Declined. Retry?' });
          // showResult('declined', JSON.stringify(response, null, '\t'));
          completeTransaction(
            transactionId,
            { txnStatus: 'declined', ...response },
            `Payment was declined. Please check card information.`
          );
        },
        async onApproval(response) {
          if (amount === 0 && tokenOnly) {
            addCard(response);
          } else {
            completeTransaction(transactionId, { txnStatus: 'approved', ...response });
          }
        }
      };
      window.PayWithConverge.open({ ssl_txn_auth_token: token }, callback);
    } catch (error) {
      console.error(error);
      onFail(
        error ||
          `There's been an error. Please try again later. If the issue persists please contact support.`
      );
    }
  };

  const completeTransaction = async (transactionId, response, msg) => {
    try {
      const res = await interimApi(
        '/api/transactions/online/sale',
        { token, invoices, invoiceToken, transactionId, transaction: response, payerId },
        navigate
      );

      const { code, redirect, error, cardId } = res.data;
      switch (code) {
        case -1:
          navigate(redirect);
          break;
        case 0:
          if (response.txnStatus === 'approved') {
            setCardId && setCardId(cardId);
            setShow({ ...show, retryTransaction: false, approved: true });

            if (transactionId) setTxnId(transactionId);

            setReceipt && setReceipt(res?.data?.receipt);

            onApproval();
            updateAmount(amount);
          } else {
            if (msg) {
              onFail(msg);
            } else if (response.errorCode) {
              onFail([
                response.errorName,
                `Error Code: ${response.errorCode}`,
                response.errorMessage
              ]);
            }
          }
          break;
        default:
          // eslint-disable-next-line max-lines
          onFail(error || DEFAULT_ERROR_MSG);
          break;
      }
    } catch (error) {
      console.error(error);
      onFail(
        error ||
          `There's been an error. Please try again later. If the issue persists please contact support.`
      );
    }
  };

  const addCard = async (response) => {
    const newShow = Object.assign({}, show);
    try {
      const res = await interimApi(
        '/api/transactions/token/add',
        {
          targetUserId: patientId,
          preAuthForm,
          onlineCard: { ...response, terminal_id: selectedVtid }
        },
        navigate
      );
      const { code, redirect, error, card } = res.data;
      switch (code) {
        case -1:
          navigate(redirect);
          break;
        case 0:
          onApproval({ card: card || {} });
          break;
        default:
          onFail(
            error ||
              `There's been an error. Please try again later. If the issue persists please contact support.`
          );
          break;
      }
    } catch (error) {
      console.error(error);
      onFail(
        error ||
          `There's been an error. Please try again later. If the issue persists please contact support.`
      );
    }
    setShow(newShow);
  };

  if (show.unavailable) {
    return (
      <div className="absolute bottom-0 left-0 right-0 top-0 z-[999] w-full flex-col items-center justify-center !gap-5 bg-white">
        <ErrorPayment
          message={ia(show?.errors) && show.errors.join(', ')}
          onTryAgain={() => {
            setShow({ ...show, errors: [], unavailable: false });
            initiateLightbox();
          }}
          onClose={onCancel}
        />
      </div>
    );
  }

  if (show.approved) {
    if (SuccessScreen) {
      return (
        <ErrorBoundary additionalText={<Alert color="success" title="Transaction Success" />}>
          <SuccessScreen transactionId={txnId} />
        </ErrorBoundary>
      );
    }

    return (
      <div className="Transaction--inprocess flex h-full flex-col items-center justify-center !gap-5">
        Approved
      </div>
    );
  }

  if (show.retryTransaction) {
    return (
      <ErrorPayment
        message={show.retryTransaction}
        onTryAgain={initiateLightbox}
        onClose={onCancel}
      />
    );
  }

  return (
    <div className="absolute bottom-0 left-0 right-0 top-0 z-[999] flex w-full flex-col items-center justify-center !gap-5 bg-[#ffffff88]  pb-4 backdrop-blur-lg">
      <Loader className="h-[32px] w-[32px]" outlined />
      <p className="text-neutral-800">Transaction in progress...</p>
      <Button color="danger" onClick={onCancel} text="Cancel" />
    </div>
  );
};

export default withErrorBoundary(LightboxWrapper);
